import { pcWallDrawNudge, isPcSkippedItem, pcGroundId } from './pc-wall-nudge.js';

/**
 * Carimbo do build, lido do <meta name="app-version">. O index.html é servido
 * com no-cache, então este valor muda a cada deploy.
 */
export function assetVersion() {
  const raw = typeof document === 'undefined'
    ? null
    : document.querySelector('meta[name="app-version"]')?.content;
  return raw && raw !== '__APP_VERSION__' ? raw : 'dev';
}

/**
 * Carimba uma URL sob /game/* com a versão do build.
 *
 * O mapa, o manifesto e as páginas do atlas ficam sob /game/*, que o _headers
 * serve com `max-age=3600`. Sem carimbo, um deploy que reempacota o atlas deixa
 * o navegador combinar **manifesto novo com página velha** por até uma hora: as
 * coordenadas passam a apontar para outro lugar da folha e cada tile desenha o
 * sprite errado — a cidade vira um mosaico aleatório. Amarrar as três URLs ao
 * mesmo carimbo faz a troca ser atômica.
 */
export function versioned(path) {
  return `${path}${path.includes('?') ? '&' : '?'}v=${encodeURIComponent(assetVersion())}`;
}

export const TILE = 32;
export const CELL = 80;

export function coordinatePattern(value, count) {
  return count > 1 ? ((value % count) + count) % count : 0;
}

export function variantKey(serverId, patternX, patternY, patternZ) {
  return `${serverId}:${patternX}:${patternY}:${patternZ}`;
}

export function buildPatternMeta(manifest) {
  const byServer = new Map();
  for (const asset of Object.values(manifest.assets || {})) {
    const sid = asset.serverId;
    if (!byServer.has(sid)) {
      byServer.set(sid, { patternX: 1, patternY: 1, patternZ: 1 });
    }
    const meta = byServer.get(sid);
    const [px, py, pz] = asset.pattern || [0, 0, 0];
    meta.patternX = Math.max(meta.patternX, px + 1);
    meta.patternY = Math.max(meta.patternY, py + 1);
    meta.patternZ = Math.max(meta.patternZ, pz + 1);
  }
  return byServer;
}

export function lookupAsset(manifest, patternMeta, serverId, x, y, z) {
  const meta = patternMeta.get(serverId);
  if (!meta) return manifest.assets?.[variantKey(serverId, 0, 0, 0)];
  const px = coordinatePattern(x, meta.patternX);
  const py = coordinatePattern(y, meta.patternY);
  const pz = coordinatePattern(z, meta.patternZ);
  return (
    manifest.assets?.[variantKey(serverId, px, py, pz)] ||
    manifest.assets?.[variantKey(serverId, 0, 0, 0)]
  );
}

export function layerForItem(serverId, drawOrder) {
  if (drawOrder.toppers?.includes(serverId)) return 'toppers';
  if (drawOrder.borders?.includes(serverId)) return 'borders';
  if (drawOrder.onbottom?.includes(serverId)) return 'onbottom';
  if (drawOrder.bottom?.includes(serverId)) return 'bottom';
  if (drawOrder.top?.includes(serverId)) return 'top';
  return 'top';
}

export function buildWalkableSet(mapDoc, blockingIds) {
  const blocking = new Set(blockingIds);
  const groundZ = mapDoc.meta.groundZ;
  const isSaffron = mapDoc.id === 'saffron';
  const blocked = new Set();

  for (const tile of mapDoc.tiles) {
    const [x, y, z, groundId, stack] = tile;
    if (z !== groundZ) continue;
    for (const entry of stack || []) {
      const id = entry[0];
      if (!id || !blocking.has(id)) continue;
      if (isSaffron && isPcSkippedItem(id, x, y)) continue;
      // A colisão acompanha o desenho: a parede empurrada bloqueia o tile para
      // onde foi, não o de origem.
      if (isSaffron) {
        const { dx, dy } = pcWallDrawNudge(id, x, y, { isGround: false });
        blocked.add(`${x + dx},${y + dy}`);
      } else {
        blocked.add(`${x},${y}`);
      }
    }
  }

  const walkable = new Set();
  for (const tile of mapDoc.tiles) {
    const [x, y, z, groundId] = tile;
    if (z !== groundZ || !groundId) continue;
    if (!blocked.has(`${x},${y}`)) walkable.add(`${x},${y}`);
  }

  return walkable;
}
